/**
 * Data export utilities for CSV and JSON export - JavaScript version
 */

import Papa from 'papaparse';

import logger from '../../utils/logger';
// Simple grid label function
const getGridLabel = (gridX, gridY) => {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  return `${letters[gridX] || gridX}${gridY + 1}`;
};

export class DataExportService {
  /**
   * Export ranking data to CSV format
   */
  exportToCSV(rankingData, filename) {
    try {
      const csvData = this.prepareCSVData(rankingData);
      const csv = Papa.unparse(csvData);
      this.downloadFile(csv, filename, 'text/csv');
      logger.log('✅ CSV export completed');
    } catch (error) {
      logger.error('CSV export error:', error);
      throw new Error(`Failed to export CSV: ${error.message}`);
    }
  }

  /**
   * Export ranking data to JSON format
   */
  exportToJSON(rankingData, filename) {
    try {
      const jsonData = this.prepareJSONData(rankingData);
      const json = JSON.stringify(jsonData, null, 2);
      this.downloadFile(json, filename, 'application/json');
      logger.log('✅ JSON export completed');
    } catch (error) {
      logger.error('JSON export error:', error);
      throw new Error(`Failed to export JSON: ${error.message}`);
    }
  }

  /**
   * Prepare data for CSV export with comprehensive business data matching reference structure
   * Note: Only includes competitors that passed category and distance filtering
   */
  prepareCSVData(rankingData) {
    logger.log('📊 [CSV EXPORT] Preparing filtered competitor data for export');
    logger.log(`📊 [CSV EXPORT] Total grid points: ${rankingData.results?.length || 0}`);
    
    // Count total unique competitors after filtering
    const allCompetitors = new Set();
    rankingData.results?.forEach(result => {
      result.businesses?.forEach(business => {
        if (business.name?.toLowerCase() !== rankingData.businessName?.toLowerCase()) {
          allCompetitors.add(business.name);
        }
      });
    });
    logger.log(`📊 [CSV EXPORT] Total filtered competitors: ${allCompetitors.size}`);
    
    const { competitors, yourAvgRating, yourTotalReviews, yourPhotoCount } = this.computeCompetitorStats(rankingData);
    const totalPoints = rankingData.results?.length || 0;
    
    // Fix grid color categorization to match actual system:
    // Green: Rank 1-3, Yellow: Rank 4-10, Red: Rank 11+, Gray: Not Ranked (null)
    const greenCount = rankingData.results?.filter(r => 
      r.targetBusinessRank !== null && r.targetBusinessRank >= 1 && r.targetBusinessRank <= 3
    ).length || 0;
    
    const yellowCount = rankingData.results?.filter(r => 
      r.targetBusinessRank !== null && r.targetBusinessRank >= 4 && r.targetBusinessRank <= 10
    ).length || 0;
    
    const redCount = rankingData.results?.filter(r => 
      r.targetBusinessRank !== null && r.targetBusinessRank >= 11
    ).length || 0;
    
    const grayCount = rankingData.results?.filter(r => 
      r.targetBusinessRank === null || r.targetBusinessRank === undefined
    ).length || 0;

    // Debug logging to verify counts
    logger.log('🎯 Grid Color Breakdown Debug:');
    logger.log(`Total Grid Points: ${totalPoints}`);
    logger.log(`Green (1-3): ${greenCount}`);
    logger.log(`Yellow (4-10): ${yellowCount}`);
    logger.log(`Red (11+): ${redCount}`);
    logger.log(`Gray (Not Ranked): ${grayCount}`);
    logger.log(`Verification: ${greenCount + yellowCount + redCount + grayCount} = ${totalPoints}`);
    
    // Log individual grid points for verification
    rankingData.results?.forEach((result, index) => {
      const rank = result.targetBusinessRank;
      let color = 'Gray';
      if (rank !== null && rank !== undefined) {
        if (rank >= 1 && rank <= 3) color = 'Green';
        else if (rank >= 4 && rank <= 10) color = 'Yellow';
        else if (rank >= 11) color = 'Red';
      }
      logger.log(`Grid ${getGridLabel(result.gridPoint.gridX, result.gridPoint.gridY)}: Rank ${rank || 'N/A'} = ${color}`);
    });
    
    // Build per-grid detailed rows with GMB scoring breakdown
    const perGridRows = (rankingData.results || []).flatMap(res => {
      const seen = new Set();
      const unique = (res.businesses || []).filter(b => {
        const id = b.placeId || `${b.name}-${b.address}`;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      }).slice(0, 20);
      
      return unique.map((b, i) => {
        const penalty = this.getRankPenalty(i + 1);
        const penaltyPct = (penalty * 100).toFixed(0) + '%';
        const penalized = typeof b.gmbScore === 'number' ? Math.max(0, b.gmbScore * (1 - penalty)) : null;
        
        // Determine grid color based on target business rank at this grid point
        let gridColor = 'Gray';
        const targetRank = res.targetBusinessRank;
        if (targetRank !== null && targetRank !== undefined) {
          if (targetRank >= 1 && targetRank <= 3) {
            gridColor = 'Green';
          } else if (targetRank >= 4 && targetRank <= 10) {
            gridColor = 'Yellow';
          } else if (targetRank >= 11) {
            gridColor = 'Red';
          }
        }
        
        return [
          getGridLabel(res.gridPoint.gridX, res.gridPoint.gridY),
          (i + 1).toString(),
          gridColor, // Add grid color classification
          targetRank !== null ? targetRank.toString() : 'Not Ranked', // Add target business rank
          b.placeId || '',
          b.name || '',
          b.address || '',
          b.primaryType || '',
          b.primaryTypeDisplayName || '',
          typeof b.rating === 'number' ? b.rating.toString() : '',
          typeof b.userRatingCount === 'number' ? b.userRatingCount.toString() : '',
          typeof b.photoCount === 'number' ? b.photoCount.toString() : '',
          b.websiteUri || b.website || '',
          b.googleMapsUri || '',
          b.internationalPhoneNumber || '',
          b.nationalPhoneNumber || '',
          Array.isArray(b.openingHours) ? b.openingHours.join(' | ') : '',
          b.businessStatus || '',
          typeof b.priceLevel === 'number' ? b.priceLevel.toString() : '',
          typeof b.latitude === 'number' ? b.latitude.toString() : '',
          typeof b.longitude === 'number' ? b.longitude.toString() : '',
          b.searchMethod || '',
          (b.deviceContext || b.deviceType || ''),
          typeof b.originalRank === 'number' ? b.originalRank.toString() : '',
          b.queryUsed || '',
          typeof b.googleScore === 'number' ? b.googleScore.toFixed(1) : '',
          typeof b.gmbScore === 'number' ? b.gmbScore.toFixed(1) : '',
          penaltyPct,
          (penalized !== null && typeof penalized === 'number') ? penalized.toFixed(1) : '',
          // GMB scoring breakdown - show points per factor
          b.gmbFactors?.namePts?.toString() || '0',
          b.gmbFactors?.addrPts?.toString() || '0',
          b.gmbFactors?.phonePts?.toString() || '0',
          b.gmbFactors?.descPts?.toString() || '0',
          b.gmbFactors?.websitePts?.toString() || '0',
          b.gmbFactors?.hoursPts?.toString() || '0',
          b.gmbFactors?.labelPts?.toString() || '0',
          b.gmbFactors?.catBasePts?.toString() || '0',
          b.gmbFactors?.catExtraPts?.toString() || '0',
          b.gmbFactors?.catCityPts?.toString() || '0',
          b.gmbFactors?.socialPts?.toString() || '0',
          b.gmbFactors?.appointmentsPts?.toString() || '0',
          b.gmbFactors?.serviceAreaPts?.toString() || '0',
          b.gmbFactors?.bookApptPts?.toString() || '0',
          b.gmbFactors?.qaPts?.toString() || '0',
          b.gmbFactors?.photosPts?.toString() || '0',
          b.gmbFactors?.reviewRatingPts?.toString() || '0',
          b.gmbFactors?.responseRatePts?.toString() || '0',
          b.gmbFactors?.reviewsPts?.toString() || '0',
          b.gmbFactors?.velocityPts?.toString() || '0',
          b.gmbFactors?.feedPts?.toString() || '0',
          b.gmbFactors?.productsPts?.toString() || '10',
          b.gmbFactors?.servicesPts?.toString() || '10',
          // Show what was actually scraped from API
          b.description || 'Not scraped',
          Array.isArray(b.categories) ? b.categories.join('; ') : (b.types ? b.types.join('; ') : 'Not scraped'),
          Array.isArray(b.socialLinks) ? b.socialLinks.join('; ') : 'Not scraped',
          Array.isArray(b.appointmentLinks) ? b.appointmentLinks.join('; ') : 'Not scraped',
          b.serviceArea ? 'Yes' : 'No',
          b.hasQA ? 'Yes' : 'No',
          Array.isArray(b.posts) ? b.posts.length.toString() : '0',
          (typeof b.reviewResponseRate === 'number' && !isNaN(b.reviewResponseRate)) ? b.reviewResponseRate.toFixed(1) + '%' : 'Not available',
          typeof b.newReviewsLast30Days === 'number' ? b.newReviewsLast30Days.toString() : 'Not available',
          // Detailed calculation explanations
          this.getPhotosCalculationDetails(b),
          this.getRatingCalculationDetails(b),
          this.getReviewsCalculationDetails(b, competitors)
        ];
      });
    });

    // Prepare comprehensive CSV data matching reference structure
    const csvData = [
      // Header information
      ['Competitor Analysis Report - Filtered Results'],
      ['Generated on', new Date().toLocaleString()],
      ['⚠️ IMPORTANT: This report shows ONLY filtered competitors'],
      ['✅ Category Filter: Only businesses matching your industry'],
      ['✅ Distance Filter: Within search radius + 0.30km tolerance'],
      ['✅ Deduplication: Unique businesses across all grid points'],
      
      // Empty row for separation
      [],
      
      // Header row
      ['Grid Position', 'Latitude', 'Longitude', 'Rank', 'Grid Color', 'Status'],
      
      // Data rows
      ...(rankingData.results || []).map(result => {
        // Determine grid color based on target business rank
        let gridColor = 'Gray';
        const targetRank = result.targetBusinessRank;
        if (targetRank !== null && targetRank !== undefined) {
          if (targetRank >= 1 && targetRank <= 3) {
            gridColor = 'Green';
          } else if (targetRank >= 4 && targetRank <= 10) {
            gridColor = 'Yellow';
          } else if (targetRank >= 11) {
            gridColor = 'Red';
          }
        }
        
        return [
          getGridLabel(result.gridPoint.gridX, result.gridPoint.gridY),
          result.gridPoint.lat?.toString() || '',
          result.gridPoint.lng?.toString() || '',
          targetRank?.toString() || 'N/A',
          gridColor,
          targetRank !== null ? 'Ranked' : 'Not Ranked'
        ];
      }),
      
      // Empty row for separation
      [],
      
      // Summary section
      ['Summary'],
      ['Business Name', rankingData.businessName || 'Unknown'],
      ['Keyword', rankingData.keyword || 'Unknown'],
      ['Center Location', `${rankingData.centerLocation?.lat || 0}, ${rankingData.centerLocation?.lng || 0}`],
      ['Timestamp', new Date(rankingData.timestamp).toLocaleDateString()],
      ['Grid Size', (rankingData.gridSize || 1).toString()],
      ['Search Radius (km)', (rankingData.searchRadius || 5).toString()],
      ['Distance Tolerance', '+0.30km buffer applied'],
      ['Category Filter', rankingData.detectedCategory || 'Auto-detected from business'],
      ['Filtered Competitors', allCompetitors.size.toString()],
      ['Average Rank', (typeof rankingData.averageRank === 'number' && !isNaN(rankingData.averageRank)) ? rankingData.averageRank.toFixed(2) : 'N/A'],
      ['Best Rank', rankingData.bestRank?.toString() || 'N/A'],
      ['Worst Rank', rankingData.worstRank?.toString() || 'N/A'],
      ['Ranked Points', (totalPoints - grayCount).toString()],
      ['Total Points', totalPoints.toString()],
      ['Coverage', totalPoints > 0 ? `${(((totalPoints - grayCount)/totalPoints)*100).toFixed(1)}%` : '0%'],
      ['Green (1-3)', `${greenCount} (${totalPoints > 0 ? ((greenCount/totalPoints)*100).toFixed(1) : '0.0'}%)`],
      ['Yellow (4-10)', `${yellowCount} (${totalPoints > 0 ? ((yellowCount/totalPoints)*100).toFixed(1) : '0.0'}%)`],
      ['Red (11+)', `${redCount} (${totalPoints > 0 ? ((redCount/totalPoints)*100).toFixed(1) : '0.0'}%)`],
      ['Gray/Not Ranked', `${grayCount} (${totalPoints > 0 ? ((grayCount/totalPoints)*100).toFixed(1) : '0.0'}%)`],

      // Your business review summary
      [],
      ['Your Business Summary'],
      ['Average Rating', (typeof yourAvgRating === 'number' && !isNaN(yourAvgRating)) ? yourAvgRating.toFixed(1) : 'N/A'],
      ['Total Reviews', typeof yourTotalReviews === 'number' ? yourTotalReviews.toString() : '0'],
      ['Photo Count (max observed)', typeof yourPhotoCount === 'number' ? yourPhotoCount.toString() : 'N/A'],

      // Competitive Ranking Leaderboard
      [],
      ['Competitive Ranking Leaderboard'],
      ['Where you stand among all competitors based on average ranking performance and GMB profile strength'],
      ['GMB scores show business profile completeness and optimization level on a 0-1000 scale'],
      ['Penalty Formula: Grid-based penalties - 7x7: Y×0.25+R×0.75+G×1.0, 9x9: Y×0.15+R×0.45+G×0.60, 12x12: Y×0.085+R×0.25+G×0.34, then Final Score = Base × (1 - penalty%)'],
      [],
      ['Rank', 'Business Name', 'Average Rank', 'GMB Score', 'GMB Score (Penalized)', 'Coverage %', 'Best Rank', 'Rating', 'Reviews', 'Performance Tier'],
      ...this.generateLeaderboard(rankingData, competitors, yourAvgRating, yourTotalReviews).map((entry, index) => [
        (index + 1).toString(),
        entry.name,
        (typeof entry.averageRank === 'number' && !isNaN(entry.averageRank)) ? entry.averageRank.toFixed(1) : 'N/A',
        (entry.gmbScore !== null && typeof entry.gmbScore === 'number' && !isNaN(entry.gmbScore)) ? entry.gmbScore.toFixed(1) : 'N/A',
        (entry.gmbScorePenalized !== null && typeof entry.gmbScorePenalized === 'number' && !isNaN(entry.gmbScorePenalized)) ? entry.gmbScorePenalized.toFixed(1) : 'N/A',
        (typeof entry.coverage === 'number' && !isNaN(entry.coverage)) ? entry.coverage.toFixed(1) : '0.0',
        entry.bestRank ? `#${entry.bestRank}` : 'N/A',
        (entry.rating !== null && typeof entry.rating === 'number' && !isNaN(entry.rating)) ? entry.rating.toFixed(1) : 'N/A',
        entry.reviews !== null ? entry.reviews.toString() : 'N/A',
        entry.performanceTier
      ]),

      // Competitors section
      [],
      ['Competitors Summary'],
      ['Name', 'Appearances', 'Coverage %', 'Average Rank', 'Best Rank', 'Average Rating', 'Total Reviews', 'Photo Count (max)', 'Average GMB Score', 'Average GMB Score (Penalized)', 'Green (1-3)', 'Yellow (4-10)', 'Red (11+)', 'Gray/Not Ranked'],
      ...competitors.map(c => [
        c.name,
        c.appearances.toString(),
        (typeof c.coverage === 'number' && !isNaN(c.coverage)) ? c.coverage.toFixed(1) : '0.0',
        (typeof c.averageRank === 'number' && !isNaN(c.averageRank)) ? c.averageRank.toFixed(1) : 'N/A',
        c.bestRank !== null ? c.bestRank.toString() : 'N/A',
        (typeof c.avgRating === 'number' && !isNaN(c.avgRating)) ? c.avgRating.toFixed(1) : 'N/A',
        typeof c.totalReviews === 'number' ? c.totalReviews.toString() : '0',
        typeof c.photoCount === 'number' ? c.photoCount.toString() : 'N/A',
        (typeof c.avgGmbScore === 'number' && !isNaN(c.avgGmbScore)) ? c.avgGmbScore.toFixed(1) : 'N/A',
        (typeof c.avgGmbScorePenalized === 'number' && !isNaN(c.avgGmbScorePenalized)) ? c.avgGmbScorePenalized.toFixed(1) : 'N/A',
        c.greenCount?.toString() || '0',
        c.yellowCount?.toString() || '0',
        c.redCount?.toString() || '0',
        c.grayCount?.toString() || '0'
      ]),

      // GMB Scoring Calculation Methodology
      [],
      ['GMB SCORING CALCULATION METHODOLOGY'],
      ['This section explains how each GMB score component is calculated'],
      [],
      ['PHOTOS POINTS CALCULATION (Max 75 points):'],
      ['• Cover Photo (≥1 photo): +5 points'],
      ['• Logo Photo (≥2 photos): +10 points'],
      ['• Volume Bonus (≥5 photos): +40 points'],
      ['• Formula: MIN(cover_pts + logo_pts + volume_pts, 75)'],
      ['• Data Source: photoCount from Google Places API'],
      [],
      ['RATING POINTS CALCULATION (Max 110 points):'],
      ['• Formula: MIN((rating - 1) × 27.5, 110)'],
      ['• Only applies if rating > 1.0'],
      ['• Examples: 2.0★ = 27.5pts, 3.0★ = 55pts, 4.0★ = 82.5pts, 5.0★ = 110pts'],
      ['• Data Source: rating from Google Places API'],
      [],
      ['REVIEWS POINTS CALCULATION (Max 140 points):'],
      ['• Formula: MIN((your_reviews / avg_competitor_reviews) × 140, 140)'],
      ['• Compares your review count against competitor average'],
      ['• Examples: Same as avg = 140pts, 2x avg = 140pts (capped), 0.5x avg = 70pts'],
      ['• Data Source: userRatingCount from Google Places API'],
      [],
      ['OTHER SCORING FACTORS:'],
      ['• Name with City: +22pts (with city) or +10pts (without city)'],
      ['• Address Completeness: +24pts (city+keyword), +12pts (address only), +2pts (no address)'],
      ['• Phone Number: +30pts (present), 0pts (absent)'],
      ['• Description: +30pts (default for all businesses)'],
      ['• Website: +5pts (present), 0pts (absent)'],
      ['• Business Hours: +14pts (present), 0pts (absent)'],
      ['• Categories: +40pts (primary), +5pts each additional (max +10pts)'],
      ['• Social Links: +10pts (any social platform detected)'],
      ['• Q&A Section: +5pts (when present in API data)'],
      ['• Service Area: +10pts (when configured)'],
      ['• Appointments: +5pts (booking links present)'],
      ['• Velocity: +12pts max based on new reviews in last 30 days'],
      ['• Response Rate: +8pts max based on review response percentage'],
      [],
      ['TOTAL GMB SCORE: Sum of all factors (capped at 1000 points max)'],
      [],

      // Detailed per-grid Top 20 businesses (full scraped fields)
      [],
      ['Per-Grid Top 20 Businesses'],
      [
        'Grid Position', 'Rank', 'Place ID', 'Name', 'Address',
        'Primary Type', 'Primary Type Name', 'Rating', 'User Ratings', 'Photo Count',
        'Website', 'Google Maps URL', 'Intl Phone', 'Local Phone', 'Opening Hours',
        'Business Status', 'Price Level', 'Latitude', 'Longitude',
        'Search Method', 'Device', 'Original Rank', 'Query Used', 'Google Score', 'GMB Score', 'Penalty %', 'GMB Score (Penalized)',
        'Name Pts', 'Addr Pts', 'Phone Pts', 'Desc Pts', 'Website Pts', 'Hours Pts', 'Label Pts',
        'Cat Base Pts', 'Cat Extra Pts', 'Cat City Pts', 'Social Pts', 'Appt Pts', 'Service Area Pts',
        'Book Pts', 'QA Pts', 'Photos Pts', 'Rating Pts', 'Response Pts', 'Reviews Pts', 'Velocity Pts', 'Feed Pts',
        'Products Pts', 'Services Pts',
        'Scraped Description', 'Scraped Categories', 'Scraped Social Links', 'Scraped Appt Links', 
        'Has Service Area', 'Has Q&A Data', 'Scraped Posts Count', 'Response Rate %', 'New Reviews 30d',
        'Photos Calc Details', 'Rating Calc Details', 'Reviews Calc Details'
      ],
      ...perGridRows
    ];
    
    return csvData;
  }

  /**
   * Prepare data for JSON export
   */
  prepareJSONData(rankingData) {
    return {
      metadata: {
        businessName: rankingData.businessName || 'Unknown',
        keyword: rankingData.keyword || 'Unknown',
        timestamp: rankingData.timestamp || new Date(),
        centerLocation: rankingData.centerLocation || { lat: 0, lng: 0 },
        searchRadius: rankingData.searchRadius || 5,
        gridSize: rankingData.gridSize || 1
      },
      summary: {
        averageRank: rankingData.averageRank,
        bestRank: rankingData.bestRank,
        worstRank: rankingData.worstRank,
        notRankedCount: rankingData.notRankedCount || 0,
        totalSearches: rankingData.results?.length || 0
      },
      gridResults: rankingData.results?.map(result => ({
        gridPosition: getGridLabel(result.gridPoint.gridX, result.gridPoint.gridY),
        coordinates: {
          lat: result.gridPoint.lat,
          lng: result.gridPoint.lng
        },
        rank: result.targetBusinessRank,
        status: result.targetBusinessRank ? 'Ranked' : 'Not Ranked'
      })) || [],
      competitors: rankingData.results?.[0]?.businesses?.slice(0, 20).map((business, index) => ({
        rank: index + 1,
        name: business.name,
        address: business.address,
        rating: business.rating,
        reviewCount: business.userRatingCount,
        phone: business.nationalPhoneNumber || business.internationalPhoneNumber,
        website: business.websiteUri,
        gmbScore: business.gmbScore,
        businessStatus: business.businessStatus
      })) || []
    };
  }

  /**
   * Download file to user's device
   */
  downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Export historical data comparison
   */
  exportHistoricalComparison(historicalRecords, filename) {
    try {
      const csvData = this.prepareHistoricalCSVData(historicalRecords);
      const csv = Papa.unparse(csvData);
      this.downloadFile(csv, filename, 'text/csv');
      logger.log('✅ Historical comparison export completed');
    } catch (error) {
      logger.error('Historical export error:', error);
      throw new Error(`Failed to export historical data: ${error.message}`);
    }
  }

  /**
   * Prepare historical data for CSV export
   */
  prepareHistoricalCSVData(historicalRecords) {
    const csvRows = [];
    
    // Add header
    csvRows.push({
      'Date': 'Date',
      'Keyword': 'Keyword',
      'Average Rank': 'Average Rank',
      'Best Rank': 'Best Rank',
      'Worst Rank': 'Worst Rank',
      'Coverage': 'Coverage %'
    });
    
    // Add historical data
    historicalRecords.forEach(record => {
      const ranking = record.ranking;
      const totalSearches = ranking.results?.length || 1;
      const rankedSearches = ranking.results?.filter(r => r.targetBusinessRank).length || 0;
      const coverage = totalSearches > 0 ? ((rankedSearches / totalSearches) * 100).toFixed(1) : '0.0';
      
      csvRows.push({
        'Date': new Date(ranking.timestamp).toLocaleDateString(),
        'Keyword': ranking.keyword,
        'Average Rank': ranking.averageRank || 'Not Ranked',
        'Best Rank': ranking.bestRank || 'Not Ranked',
        'Worst Rank': ranking.worstRank || 'Not Ranked',
        'Coverage': coverage + '%'
      });
    });
    
    return csvRows;
  }

  /**
   * Compute competitors stats including average rating and reviews
   */
  computeCompetitorStats(rankingData) {
    const competitorCounts = new Map();
    const competitorPositions = new Map();
    const competitorRatings = new Map();
    const competitorReviewCounts = new Map();
    const competitorPhotoCounts = new Map();
    const competitorGmbScores = new Map();

    const yourRatings = [];
    const yourReviewCounts = [];
    const yourPhotoCounts = [];

    // Process all search results
    for (const result of rankingData.results || []) {
      for (const [idx, b] of (result.businesses || []).entries()) {
        const isYou = b.name?.toLowerCase() === rankingData.businessName?.toLowerCase();
        
        if (isYou) {
          if (typeof b.rating === 'number') yourRatings.push(b.rating);
          if (typeof b.userRatingCount === 'number') yourReviewCounts.push(b.userRatingCount);
          if (typeof b.photoCount === 'number') yourPhotoCounts.push(b.photoCount);
        } else {
          competitorCounts.set(b.name, (competitorCounts.get(b.name) || 0) + 1);
          if (!competitorPositions.has(b.name)) competitorPositions.set(b.name, []);
          competitorPositions.get(b.name).push(idx + 1);
          
          if (typeof b.rating === 'number') {
            if (!competitorRatings.has(b.name)) competitorRatings.set(b.name, []);
            competitorRatings.get(b.name).push(b.rating);
          }
          if (typeof b.userRatingCount === 'number') {
            if (!competitorReviewCounts.has(b.name)) competitorReviewCounts.set(b.name, []);
            competitorReviewCounts.get(b.name).push(b.userRatingCount);
          }
          if (typeof b.photoCount === 'number') {
            if (!competitorPhotoCounts.has(b.name)) competitorPhotoCounts.set(b.name, []);
            competitorPhotoCounts.get(b.name).push(b.photoCount);
          }
          if (typeof b.gmbScore === 'number') {
            if (!competitorGmbScores.has(b.name)) competitorGmbScores.set(b.name, []);
            competitorGmbScores.get(b.name).push(b.gmbScore);
          }
        }
      }
    }

    const totalPoints = rankingData.results?.length || 1;
    const competitors = Array.from(competitorCounts.entries())
      .map(([name, appearances]) => {
        const positions = competitorPositions.get(name) || [];
        const averageRank = positions.length ? positions.reduce((a, b) => a + b, 0) / positions.length : 0;
        const bestRank = positions.length ? Math.min(...positions) : null;
        const coverage = (appearances / totalPoints) * 100;
        const ratings = competitorRatings.get(name) || [];
        const avgRating = ratings.length ? ratings.reduce((a, b) => a + b, 0) / ratings.length : null;
        const reviews = competitorReviewCounts.get(name) || [];
        const totalReviews = reviews.length ? Math.max(...reviews) : null;
        const photos = competitorPhotoCounts.get(name) || [];
        const photoCount = photos.length ? Math.max(...photos) : null;
        const gmbScores = competitorGmbScores.get(name) || [];
        const avgGmbScore = gmbScores.length ? gmbScores.reduce((a, b) => a + b, 0) / gmbScores.length : null;
        
        // Calculate grid color counts based on target business rank at each grid point
        // Count grid points by color classification, considering competitor presence
        let greenCount = 0, yellowCount = 0, redCount = 0, grayCount = 0;
        
        for (const result of rankingData.results || []) {
          const targetRank = result.targetBusinessRank;
          const competitorAppearsInThisGrid = (result.businesses || []).some(b => b.name === name);
          
          // Classify this grid point by color based on target business rank
          let gridColor = 'gray';
          if (targetRank !== null && targetRank !== undefined) {
            if (targetRank >= 1 && targetRank <= 3) {
              gridColor = 'green';
            } else if (targetRank >= 4 && targetRank <= 10) {
              gridColor = 'yellow';
            } else if (targetRank >= 11) {
              gridColor = 'red';
            }
          }
          
          // Count based on whether competitor appears in this grid point
          if (competitorAppearsInThisGrid) {
            // Competitor appears in this grid - count the grid color
            if (gridColor === 'green') greenCount++;
            else if (gridColor === 'yellow') yellowCount++;
            else if (gridColor === 'red') redCount++;
            else grayCount++;
          } else {
            // Competitor doesn't appear in this grid - always counts as gray for this competitor
            grayCount++;
          }
        }
        
        // Debug logging for verification
        logger.log(`🔍 ${name} Color Breakdown:`);
        logger.log(`  Appearances: ${appearances}/${totalPoints}`);
        logger.log(`  Green: ${greenCount}, Yellow: ${yellowCount}, Red: ${redCount}, Gray: ${grayCount}`);
        logger.log(`  Total Check: ${greenCount + yellowCount + redCount + grayCount} should equal ${totalPoints}`);
        
        // Grid-based penalty values
        const getPenaltyValues = (gridSize) => {
          if (gridSize === 49) return { yellow: 0.25, red: 0.75, gray: 1.0 }; // 7x7
          if (gridSize === 81) return { yellow: 0.15, red: 0.45, gray: 0.60 }; // 9x9
          if (gridSize === 144) return { yellow: 0.085, red: 0.25, gray: 0.34 }; // 12x12
          return { yellow: 0.25, red: 0.75, gray: 1.0 }; // Default to 7x7
        };
        
        const penalties = getPenaltyValues(totalPoints);
        
        // New penalty formula: (Yellow×penalty + Red×penalty + Gray×penalty) / 100
        const penaltyPercentage = (yellowCount * penalties.yellow + redCount * penalties.red + grayCount * penalties.gray) / 100;
        const avgGmbScorePenalized = avgGmbScore !== null ? Math.max(0, avgGmbScore * (1 - penaltyPercentage)) : null;
        
        return { 
          name, 
          appearances, 
          averageRank, 
          bestRank, 
          coverage, 
          avgRating, 
          totalReviews, 
          photoCount, 
          avgGmbScore,
          avgGmbScorePenalized,
          greenCount,
          yellowCount,
          redCount,
          grayCount
        };
      })
      .sort((a, b) => a.averageRank - b.averageRank);

    const yourAvgRating = yourRatings.length ? yourRatings.reduce((a, b) => a + b, 0) / yourRatings.length : null;
    const yourTotalReviews = yourReviewCounts.length ? Math.max(...yourReviewCounts) : null;
    const yourPhotoCount = yourPhotoCounts.length ? Math.max(...yourPhotoCounts) : null;

    return { competitors, yourAvgRating, yourTotalReviews, yourPhotoCount };
  }

  /**
   * Get detailed business rows for all grid points
   */
  getDetailedBusinessRows(rankingData) {
    const detailedRows = [];
    
    for (const result of rankingData.results || []) {
      const gridLabel = getGridLabel(result.gridPoint.gridX, result.gridPoint.gridY);
      
      for (const [idx, business] of (result.businesses || []).slice(0, 20).entries()) {
        detailedRows.push([
          gridLabel,
          (idx + 1).toString(),
          business.placeId || '',
          business.name || '',
          business.address || '',
          business.primaryType || '',
          typeof business.rating === 'number' ? business.rating.toString() : '',
          typeof business.userRatingCount === 'number' ? business.userRatingCount.toString() : '',
          typeof business.photoCount === 'number' ? business.photoCount.toString() : '',
          business.websiteUri || business.website || '',
          business.nationalPhoneNumber || business.internationalPhoneNumber || business.phone || '',
          Array.isArray(business.openingHours) ? business.openingHours.join(' | ') : '',
          business.businessStatus || '',
          typeof business.priceLevel === 'number' ? business.priceLevel.toString() : '',
          typeof business.latitude === 'number' ? business.latitude.toString() : '',
          typeof business.longitude === 'number' ? business.longitude.toString() : '',
          (typeof business.gmbScore === 'number' && !isNaN(business.gmbScore)) ? business.gmbScore.toFixed(1) : '',
          Array.isArray(business.categories) ? business.categories.join('; ') : (business.types ? business.types.join('; ') : ''),
          Array.isArray(business.socialLinks) ? business.socialLinks.join('; ') : '',
          business.hasQA ? 'Yes' : 'No',
          business.serviceArea ? 'Yes' : 'No',
          (typeof business.reviewResponseRate === 'number' && !isNaN(business.reviewResponseRate)) ? business.reviewResponseRate.toFixed(1) + '%' : ''
        ]);
      }
    }
    
    return detailedRows;
  }

  /**
   * Get per-grid rank penalty to subtract from GMB score
   * Formula: 1 × penalty = penalty result
   */
  getRankPenalty(rank) {
    if (!rank || rank <= 0) return 0;     // 1 × 0 = 0
    if (rank <= 3) return 0;              // 1 × 0 = 0 (Green: ranks 1-3)
    if (rank <= 10) return 0.25;          // 1 × 0.25 = 0.25 (Yellow: ranks 4-10)
    if (rank <= 20) return 0.50;          // 1 × 0.50 = 0.50 (Red: ranks 11-20)
    return 0.75;                          // 1 × 0.75 = 0.75 (Gray: ranks 21+)
  }

  /**
   * Get performance tier based on average rank
   */
  getPerformanceTier(averageRank) {
    if (averageRank <= 3) return 'Excellent';
    if (averageRank <= 7) return 'Good';
    if (averageRank <= 15) return 'Fair';
    return 'Needs Improvement';
  }

  /**
   * Generate competitive ranking leaderboard
   */
  generateLeaderboard(rankingData, competitors, yourAvgRating, yourTotalReviews) {
    // Calculate your business stats
    const yourPositions = [];
    const yourGmbScores = [];
    const yourGmbScoresPenalized = [];
    let yourAppearances = 0;
    
    (rankingData.results || []).forEach(result => {
      (result.businesses || []).slice(0, 20).forEach((b, idx) => {
        const isYou = b.name?.toLowerCase() === rankingData.businessName?.toLowerCase();
        if (isYou) {
          yourPositions.push(idx + 1);
          yourAppearances++;
          if (typeof b.gmbScore === 'number') {
            yourGmbScores.push(b.gmbScore);
            const penalty = this.getRankPenalty(idx + 1);
            yourGmbScoresPenalized.push(Math.max(0, b.gmbScore * (1 - penalty)));
          }
        }
      });
    });

    const yourAverageRank = yourPositions.length ? yourPositions.reduce((a, b) => a + b, 0) / yourPositions.length : 999;
    const yourBestRank = yourPositions.length ? Math.min(...yourPositions) : null;
    const yourCoverage = ((rankingData.results?.length || 1) > 0) ? (yourAppearances / (rankingData.results?.length || 1)) * 100 : 0;
    const yourAvgGmbScore = yourGmbScores.length ? yourGmbScores.reduce((a, b) => a + b, 0) / yourGmbScores.length : null;
    const yourAvgGmbScorePenalized = yourGmbScoresPenalized.length ? yourGmbScoresPenalized.reduce((a, b) => a + b, 0) / yourGmbScoresPenalized.length : null;

    // Create leaderboard entries
    const leaderboardEntries = [
      // Your business
      {
        name: `${rankingData.businessName || 'Your Business'} (YOU)`,
        averageRank: yourAverageRank,
        bestRank: yourBestRank,
        coverage: yourCoverage,
        rating: yourAvgRating,
        reviews: yourTotalReviews,
        gmbScore: yourAvgGmbScore,
        gmbScorePenalized: yourAvgGmbScorePenalized,
        performanceTier: this.getPerformanceTier(yourAverageRank)
      },
      // All competitors
      ...competitors.map(c => ({
        name: c.name,
        averageRank: c.averageRank,
        bestRank: c.bestRank,
        coverage: c.coverage,
        rating: c.avgRating,
        reviews: c.totalReviews,
        gmbScore: c.avgGmbScore,
        gmbScorePenalized: c.avgGmbScorePenalized,
        performanceTier: this.getPerformanceTier(c.averageRank)
      }))
    ];

    // Sort by penalized GMB score (higher is better), then raw GMB score, then average rank
    return leaderboardEntries.sort((a, b) => {
      const aPen = (a.gmbScorePenalized ?? -1);
      const bPen = (b.gmbScorePenalized ?? -1);
      if (bPen !== aPen) return bPen - aPen;

      const aRaw = (a.gmbScore ?? -1);
      const bRaw = (b.gmbScore ?? -1);
      if (bRaw !== aRaw) return bRaw - aRaw;

      return a.averageRank - b.averageRank;
    });
  }

  /**
   * Get detailed photos calculation explanation
   */
  getPhotosCalculationDetails(business) {
    const photoCount = business.photoCount || 0;
    let details = `Photos: ${photoCount} | `;
    let totalPts = 0;
    
    if (photoCount >= 1) {
      details += 'Cover: +5pts | ';
      totalPts += 5;
    }
    if (photoCount >= 2) {
      details += 'Logo: +10pts | ';
      totalPts += 10;
    }
    if (photoCount >= 5) {
      details += 'Volume: +40pts | ';
      totalPts += 40;
    }
    
    const finalPts = Math.min(totalPts, 75);
    details += `Total: ${finalPts}/75pts`;
    
    if (photoCount === 0) {
      details = 'No photos found | Total: 0/75pts';
    }
    
    return details;
  }

  /**
   * Get detailed rating calculation explanation
   */
  getRatingCalculationDetails(business) {
    const rating = business.rating || 0;
    
    if (rating <= 1) {
      return `Rating: ${rating}★ | Below 1.0 threshold | Total: 0/110pts`;
    }
    
    const calculatedPts = (rating - 1) * 27.5;
    const finalPts = Math.min(calculatedPts, 110);
    
    return `Rating: ${(typeof rating === 'number' && !isNaN(rating)) ? rating.toFixed(1) : '0.0'}★ | Formula: (${(typeof rating === 'number' && !isNaN(rating)) ? rating.toFixed(1) : '0.0'} - 1) × 27.5 = ${(typeof calculatedPts === 'number' && !isNaN(calculatedPts)) ? calculatedPts.toFixed(1) : '0.0'} | Total: ${(typeof finalPts === 'number' && !isNaN(finalPts)) ? finalPts.toFixed(1) : '0.0'}/110pts`;
  }

  /**
   * Get detailed reviews calculation explanation
   */
  getReviewsCalculationDetails(business, competitors) {
    const myReviews = business.userRatingCount || 0;
    
    if (myReviews === 0) {
      return 'Reviews: 0 | No reviews found | Total: 0/140pts';
    }
    
    const competitorReviews = competitors
      .map(c => c.totalReviews || 0)
      .filter(count => count > 0);
    
    if (competitorReviews.length === 0) {
      return `Reviews: ${myReviews} | No competitor data | Total: 0/140pts`;
    }
    
    const avgCompetitorReviews = competitorReviews.reduce((sum, count) => sum + count, 0) / competitorReviews.length;
    const ratio = myReviews / avgCompetitorReviews;
    const calculatedPts = ratio * 140;
    const finalPts = Math.min(calculatedPts, 140);
    
    return `Reviews: ${myReviews} | Competitor Avg: ${(typeof avgCompetitorReviews === 'number' && !isNaN(avgCompetitorReviews)) ? avgCompetitorReviews.toFixed(0) : '0'} | Ratio: ${(typeof ratio === 'number' && !isNaN(ratio)) ? ratio.toFixed(2) : '0.00'} | Formula: ${(typeof ratio === 'number' && !isNaN(ratio)) ? ratio.toFixed(2) : '0.00'} × 140 = ${(typeof calculatedPts === 'number' && !isNaN(calculatedPts)) ? calculatedPts.toFixed(1) : '0.0'} | Total: ${(typeof finalPts === 'number' && !isNaN(finalPts)) ? finalPts.toFixed(1) : '0.0'}/140pts`;
  }
}